// @flow
declare var __ELECTRON__: boolean;

import { TRACK_URL_EXPIRY_MILLISECONDS as expiry } from '../constants';
import {
  selectWebappClientVersion,
  selectElectronClientVersion,
  selectDeviceId,
  selectUseMP3EncryptedPlayer,
  selectUseM3U8EncryptedPlayer,
} from '../selectors/client';
import { loaded, notExpired } from '../middleware/apiCacheValidators';
import { selectAccessToken } from '../selectors/auth';
import { selectSonosIsConnected } from '../selectors/sonos';
import type { ThunkAction } from './types';
import { selectTrackUrl, selectDefaultTrackUrlResolver } from '../selectors/trackUrls';

import type { AudioQuality } from './client';

export const getCompoundId = (trackId: string, quality: AudioQuality) =>
  [trackId, quality].join('-');
export type FetchTrackUrlAction = {
  type: 'FETCH_TRACK_URL',
  phase: 'REQUEST' | 'SUCCESS' | 'FAILURE',
  trackId: string,
  quality: number,
  compoundId: string,
  requestedAt?: number,
  trackUrl?: string,
  loadedAt?: number,
  expireAt?: number,
};

export type InvalidateTrackUrlAction = {
  type: 'INVALIDATE_TRACK_URL',
  compoundId: string,
};

export type TrackUrlAction = FetchTrackUrlAction | InvalidateTrackUrlAction;

// We remember promises that are pending so we don't redo them.
// When a promise that is in-progress gets called again (by user action for example)
// we just return the same one instead of a newly generated one.
const promiseCache = {};

export function loadTrackUrl(trackId: string, quality: number): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const compoundId = getCompoundId(trackId, quality);
    const rest = { trackId, quality, compoundId };

    if (promiseCache[compoundId]) {
      return promiseCache[compoundId];
    }

    const cachedTrackUrl = selectTrackUrl(state, compoundId);
    const cacheIsValid = loaded(cachedTrackUrl) && notExpired(cachedTrackUrl);
    if (cacheIsValid) {
      return cachedTrackUrl.url;
    }

    dispatch({
      type: 'FETCH_TRACK_URL',
      phase: 'REQUEST',
      requestedAt: Date.now(),
      ...rest,
    });

    try {
      const trackUrlResolver = selectDefaultTrackUrlResolver(state);
      const promise = trackUrlResolver({
        id: trackId,
        quality,
        accessToken: selectAccessToken(state),
        clientVersion: __ELECTRON__
          ? selectElectronClientVersion(state)
          : selectWebappClientVersion(state),
        deviceId: selectDeviceId(state),
        sonosIsConnected: selectSonosIsConnected(state),
        useMP3EncryptedPlayer: selectUseMP3EncryptedPlayer(state),
        useM3U8EncryptedPlayer: selectUseM3U8EncryptedPlayer(state),
      });
      promiseCache[compoundId] = promise;

      const trackUrl = await promise;
      delete promiseCache[compoundId];

      const loadedAt = Date.now();
      const expireAt = expiry < 0 ? -expiry : loadedAt + expiry;

      dispatch({
        type: 'FETCH_TRACK_URL',
        phase: 'SUCCESS',
        trackUrl,
        loadedAt,
        expireAt,
        ...rest,
      });

      return trackUrl;
    } catch (error) {
      dispatch({
        type: 'FETCH_TRACK_URL',
        phase: 'FAILURE',
        ...rest,
      });
      delete promiseCache[compoundId];

      throw error;
    }
  };
}

export function invalidateTrackUrl(
  trackId: string,
  quality: AudioQuality
): InvalidateTrackUrlAction {
  const compoundId = getCompoundId(trackId, quality);

  if (promiseCache[compoundId]) {
    delete promiseCache[compoundId];
  }

  return {
    type: 'INVALIDATE_TRACK_URL',
    compoundId,
  };
}

export function getStateResolver(dispatch: Dispatch) {
  return (trackId: string, quality: number) => dispatch(loadTrackUrl(trackId, quality));
}
